import _ from 'lodash';
import React from 'react';
import { Table, Tooltip, Typography } from 'antd';
import {
  CloseCircleFilled,
  CheckCircleFilled,
  ExclamationCircleFilled,
} from '@ant-design/icons';

import { StyledModal } from 'common/components';
import { useGetEntityProperties } from 'hooks/entityHooks';
import { covertNestedArrToSingleArr } from 'pages/minimum-requirements/components/modal/utils';
import { createErrorMapByProduct, generateErrorFieldList } from './utils';

import './ValidateResultModal.less';

const DEFAULT_COLUMNS = [
  {
    title: 'UPC',
    dataIndex: 'upc',
    width: 150,
  },
  {
    title: 'Package Level',
    dataIndex: 'packageLevel',
    width: 150,
  },
];

const PRODUCT_ID_COLUMN = [
  {
    title: 'Product ID',
    dataIndex: 'productId',
    width: 150,
  },
];

const ValidateResultModal = ({
  validateResultModalState,
  setValidateResultModalState,
  onSubmit,
  loading,
  isBulkEdit = false,
}) => {
  const { getEntityProperties } = useGetEntityProperties({
    fetchOnMount: true,
  });

  const entityProperties = getEntityProperties();
  const { properties } = covertNestedArrToSingleArr(entityProperties);

  const productsWithErrors = (validateResultModalState?.data || []).filter(
    (product) => product.isSuccess === false
  );

  const errorFields =
    generateErrorFieldList(productsWithErrors, properties) || [];
  const errorMapByProduct = createErrorMapByProduct(productsWithErrors);

  const mappingColumns = errorFields?.map((item) => ({
    width: 200,
    title: (
      <Tooltip title={item?.fullPathDisplayName || item?.displayName}>
        <Typography.Text style={{ fontSize: 12 }}>
          {item?.displayName}
        </Typography.Text>
      </Tooltip>
    ),
    dataIndex: item?.fieldFullPath,
    key: item?.fieldFullPath,
    render: (_, record) => (
      <RenderValue
        displayName={item?.displayName}
        productKey={`${record.upc || record.productId}_${record.packageLevel}`}
        fieldFullPath={item?.fieldFullPath}
        errorMapByProduct={errorMapByProduct}
        columnPackageLevel={item?.packageLevel}
      />
    ),
    ellipsis: true,
  }));

  const columns = [
    ...(isBulkEdit ? PRODUCT_ID_COLUMN : []),
    ...DEFAULT_COLUMNS,
    ...mappingColumns,
  ];

  const modalProps = {
    title: 'Validation Failed',
    visible: validateResultModalState?.visible,
    destroyOnClose: true,
    width: 1000,
    bodyStyle: {
      minHeight: '400px',
      overflow: 'auto',
    },
    centered: true,
    okText: 'Continue',
    maskClosable: false,
    confirmLoading: loading,
    onCancel: () => {
      setValidateResultModalState({
        visible: false,
        data: null,
        sessionId: null,
      });
    },
    onOk: onSubmit,
    wrapClassName: 'validation-failed-modal',
  };

  return (
    <StyledModal {...modalProps}>
      <div style={{ width: '100%' }}>
        <Table
          columns={columns}
          dataSource={productsWithErrors}
          pagination={false}
          scroll={{ y: 290, x: 930 }}
          bordered
        />
      </div>
      <div
        style={{
          textAlign: 'center',
          width: '100%',
          marginTop: 8,
        }}
      >
        <Typography.Text style={{ color: '#ff4d4f', fontSize: 14 }}>
          The following item(s) have not passed data validations, and can not be
          set to Active at this time. If you continue, the item(s) will be set
          to Pending
        </Typography.Text>
      </div>
    </StyledModal>
  );
};

export default ValidateResultModal;

const RenderValue = ({
  productKey,
  fieldFullPath,
  displayName,
  errorMapByProduct,
  columnPackageLevel,
}) => {
  const productData = errorMapByProduct.get(productKey);
  if (!productData) return null;

  const { errorMap, packageLevel } = productData;
  const fieldKey = fieldFullPath?.toLowerCase();
  const errorField = errorMap.get(fieldKey);

  const hasError = !!errorField;

  if (hasError) {
    const errorMessage =
      errorField.errorMessage || `${displayName || fieldFullPath} is required.`;

    const isWarning = errorField?.isWarning;

    return (
      <div style={{ width: '100%', height: '100%' }}>
        {isWarning ? (
          <ExclamationCircleFilled style={{ color: '#faad14' }} />
        ) : (
          <CloseCircleFilled style={{ color: '#ff7875' }} />
        )}{' '}
        <Typography.Text
          ellipsis={{ tooltip: errorMessage }}
          style={{
            fontSize: 12,
            color: isWarning ? '#faad14' : undefined,
          }}
        >
          {errorMessage}
        </Typography.Text>
      </div>
    );
  }

  if (columnPackageLevel === packageLevel) {
    return (
      <div style={{ width: '100%', height: '100%' }}>
        <CheckCircleFilled style={{ color: '#73d13d' }} />
      </div>
    );
  }

  return (
    <div
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: '#f5f5f5',
      }}
    />
  );
};
